<template>
  <div class="profit-results">
    <div class="profit-results__description">
      <i18n path="calculator.profit-calculator-results.description" tag="div">
        <template #totalTime>
          <span class="profit-results__description--highlight">{{ totalTime }}</span>
        </template>
        <template #profit>
          <span class="profit-results__description--highlight">{{ endAmount }}</span>
        </template>
        <template #profitChange>
          <ProfitChange :startAmount="props.startData.predictedValue" :endAmount="props.endData.predictedValue" />
        </template>
      </i18n>
    </div>
    <div class="profit-results__list">
      <div class="profit-results-list-item">
        <div class="profit-results-list-item__date">
          {{ startDate.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
          }) }}
        </div>
        <div class="profit-results-list-item__results">
          <div class="profit-results-list-item__results-label">
            {{ $t('calculator.profit-result.start-amount') }}
          </div>
          <div class="profit-results-list-item__amount">
            {{ startAmount }}
            <div class="profit-results-list-item__amount-crypto">
              {{ startCryptoAmount }} {{ cryptoCurrency.code }}
            </div>
          </div>
        </div>
      </div>
      <div class="profit-results-list-item">
        <div class="profit-results-list-item__date">
          {{ endDate.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
          }) }}
        </div>
        <div class="profit-results-list-item__results">
          <div class="profit-results-list-item__results-label">
            {{ $t('calculator.profit-result.projected-amount') }}
          </div>
          <div class="profit-results-list-item__amount">
            {{ endAmount }}
            <div class="profit-results-list-item__amount-crypto">
              {{ endCryptoAmount }} {{ cryptoCurrency.code }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="profit-results__cta">
      <CallToAction :cryptoCurrency="props.cryptoCurrency" />
    </div>
  </div>
</template>

<script setup lang="ts">
import ProfitChange from "@kano/components/ProfitCalculator/ProfitChange.vue";
import { computed } from "vue";
import { i18n as i18nInstance } from "@kano/i18n";
import { formatNumberStandard, CurrencyFormat } from "@kano/helpers/numberFormatter";
import { ProfitCalculatorCurrency } from "@kano/types/profit-calculator";
import CallToAction from "@kano/components/ProfitCalculator/CallToAction.vue";

const props = defineProps<{
  fiatCurrency: ProfitCalculatorCurrency,
  cryptoCurrency: ProfitCalculatorCurrency,
  startData: Object,
  endData: Object,
}>();

const startAmount = computed(() => {
  return formatAmount(props.startData.predictedValue, props.fiatCurrency.code);
})
const startCryptoAmount = computed(() => {
  return formatAmount(props.startData.predictedValue/props.startData.predictedPrice);
})
const startDate = computed(() => {
  return new Date(props.startData.date);
})
const endAmount = computed(() => {
  return formatAmount(props.endData.predictedValue, props.fiatCurrency.code);
})
const endCryptoAmount = computed(() => {
  return formatAmount(props.endData.predictedValue/props.endData.predictedPrice);
})
const endDate = computed(() => {
  return new Date(props.endData.date);
})

const totalTime = computed(() => {
  let years = endDate.value.getFullYear() - startDate.value.getFullYear();
  let months = endDate.value.getMonth() - startDate.value.getMonth();

  // Adjust if months are negative (meaning the end month is before start month)
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  // Create a textual representation (e.g., "1 year 3 months")
  const result = [];
  if (years > 0) {
    result.push(i18nInstance.tc('calculator.profit-result.year', years, { n: years }));
  }
  if (months > 0) {
    result.push(i18nInstance.tc('calculator.profit-result.month', months, { n: months }));
  }
  if (result.length === 0) {
    result.push(i18nInstance.tc('calculator.profit-result.month', months, { n: months }));
  }

  return result.join(' ');
});

const formatAmount = (amount: number, currencyCode: string) => {
  return formatNumberStandard(amount, i18nInstance.locale, currencyCode, CurrencyFormat.symbol);
};
</script>

<style scoped lang="scss">
@use '@kanoAssets/styles/breakpoints.scss';

.profit-results {
  &__description {
    text-align: center;
    color: var(--color-base-48);
  }

  &__description--highlight {
    color: var(--color-base);
  }

  &__list {
    padding: 32px 0;

    @include breakpoints.desktop {
      padding: 48px 0;
    }
  }

  &__cta {
    display: flex;
    justify-content: center;
  }
}

.profit-results-list-item {
  margin-bottom: 16px;

  &__date {
    padding-bottom: 12px;
    border-bottom: solid 1px var(--color-base-8);
    font-size: 14px;
    line-height: 20px;
    color: var(--color-base-64);
  }

  &__results {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-top: 12px;
    font-size: 16px;
    line-height: 24px;
  }

  &__amount {
    text-align: end;
  }

  &__amount-crypto {
    color: var(--color-base-64);
    font-size: 14px;
    line-height: 20px;
  }
}
</style>